import type { DeltaBase, MetricValue } from '@ops/contracts'
import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { formatMetricValue } from '../lib/format'
import { Card } from './Card'
import { DeltaTag } from './DeltaTag'
import { EmptyMetricState } from './EmptyMetricState'
import { HealthDot } from './HealthDot'
import { ProvenanceBadge } from './ProvenanceBadge'

/**
 * El número por el que se abre el panel, con su subordinado adentro.
 *
 * Injerto del briefing ejecutivo, y arregla un agujero del tablero de riesgo:
 * riesgo colapsaba el MRR en un bloque plegado al final. Acá el protagonista va
 * arriba, con el secundario (ARR) dentro de la misma tarjeta y **la fórmula
 * visible**, así el número se puede recalcular a mano.
 *
 * `aside` es la columna derecha: normalmente el gráfico de tendencia. Cuando
 * falta, la tarjeta ocupa todo el ancho y sigue leyéndose.
 */
export interface HeroKpiProps {
  metric: MetricValue
  /** Métrica subordinada, dentro de la misma tarjeta. Ej. ARR dentro de MRR. */
  secundaria?: MetricValue | null
  /** Base del delta del protagonista. La regla de derivables la exige. */
  base?: DeltaBase | null
  direccion?: 'higher_better' | 'lower_better'
  /** Columna derecha: el gráfico de tendencia del período. */
  aside?: ReactNode
  /** Acciones al pie de la columna izquierda. */
  acciones?: ReactNode
  /** Nivel del heading del nombre. 2 por defecto, mismo patrón que `Section`. */
  nivel?: 2 | 3 | 4
  onLoadData?: () => void
  now?: Date
  className?: string
}

export function HeroKpi({
  metric,
  secundaria,
  base,
  direccion,
  aside,
  acciones,
  onLoadData,
  nivel = 2,
  now,
  className,
}: HeroKpiProps) {
  const sinDato = metric.valor === null
  const Heading = `h${nivel}` as const

  return (
    <Card
      as="section"
      ariaLabel={metric.nombre}
      padding="none"
      elevada
      className={cx(
        'grid grid-cols-1 overflow-hidden',
        aside !== undefined && 'lg:grid-cols-[minmax(300px,440px)_minmax(0,1fr)]',
        className,
      )}
    >
      <div
        className={cx(
          'min-w-0 p-5',
          aside !== undefined &&
            'border-b border-border lg:border-b-0 lg:border-r',
        )}
      >
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Heading className="rotulo text-meta">{metric.nombre}</Heading>
          <HealthDot health={metric.health} size="sm" />
        </div>

        {sinDato ? (
          <EmptyMetricState
            metricNombre={metric.nombre}
            onLoadData={onLoadData}
            size="lg"
            detalle="Es el número por el que se abre el panel: sin él no hay lectura del mes."
          />
        ) : (
          <p
            data-testid="hero-kpi-valor"
            className="tabular text-hero font-semibold text-text"
          >
            {formatMetricValue(metric.valor, metric.unidad)}
          </p>
        )}

        {!sinDato ? (
          <div className="mt-2">
            <DeltaTag
              deltaPct={metric.deltaPct}
              base={base}
              unidad={metric.unidad}
              direccion={direccion}
              size="lg"
            />
          </div>
        ) : null}

        <div className="mt-3">
          <ProvenanceBadge provenance={metric.provenance} now={now} />
        </div>

        {metric.formula !== null ? (
          <p className="mt-3 text-micro text-muted-strong">
            <span className="rotulo mr-1.5">Fórmula</span>
            <code
              data-testid="hero-kpi-formula"
              className="rounded border border-border bg-n100 px-1.5 py-0.5 font-mono text-micro text-text"
            >
              {metric.formula}
            </code>
          </p>
        ) : null}

        {secundaria !== null && secundaria !== undefined ? (
          <div
            data-testid="hero-kpi-secundaria"
            className="mt-5 border-t border-border pt-4"
          >
            <p className="rotulo mb-1">{secundaria.nombre}</p>
            {secundaria.valor === null ? (
              <EmptyMetricState
                metricNombre={secundaria.nombre}
                onLoadData={onLoadData}
                size="sm"
              />
            ) : (
              <p className="tabular text-seccion font-semibold text-text">
                {formatMetricValue(secundaria.valor, secundaria.unidad)}
              </p>
            )}
            {/* La secundaria es un número del panel como cualquier otro: lleva
                su propia fuente y frescura. El badge del protagonista está del
                otro lado de la línea divisoria y hoy coincide por casualidad;
                el día que ARR se cargue a mano mientras MRR sincroniza con
                Stripe, atribuirle el badge de arriba sería mentir. */}
            <div className="mt-2">
              <ProvenanceBadge
                provenance={secundaria.provenance}
                now={now}
                variant="inline"
              />
            </div>
            {secundaria.formula !== null ? (
              <p className="mt-1.5 text-micro text-muted-strong">
                <code className="rounded border border-border bg-n100 px-1.5 py-0.5 font-mono text-micro text-text">
                  {secundaria.formula}
                </code>
              </p>
            ) : null}
          </div>
        ) : null}

        {acciones !== undefined ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">{acciones}</div>
        ) : null}
      </div>

      {aside !== undefined ? (
        <div className="min-w-0 p-4">{aside}</div>
      ) : null}
    </Card>
  )
}
